/**
 * Collection Filters
 * Client-side filtering for collection pages using Linnworks metafields
 */

class CollectionFilters {
  constructor() {
    this.filters = {
      genre: [],
      decade: [],
      actors: [],
      franchises: []
    };
    this.productCards = [];
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Cache product cards
    this.productCards = Array.from(document.querySelectorAll('.product-card'));

    // Only initialize if we have product cards
    if (this.productCards.length === 0) {
      return;
    }

    this.bindFilterEvents();
    this.bindClearFilters();
    this.bindActorSearch();
    this.updateResultCount();
  }

  bindFilterEvents() {
    const checkboxes = document.querySelectorAll('[data-filter]');

    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const filterType = e.target.dataset.filter;
        const value = e.target.value;

        if (e.target.checked) {
          // Add to filter array
          if (!this.filters[filterType].includes(value)) {
            this.filters[filterType].push(value);
          }
        } else {
          // Remove from filter array
          this.filters[filterType] = this.filters[filterType].filter(v => v !== value);
        }

        this.applyFilters();
      });
    });
  }

  bindClearFilters() {
    const clearButton = document.querySelector('.clear-filters');

    if (!clearButton) return;

    clearButton.addEventListener('click', () => {
      // Reset all filter arrays
      this.filters = {
        genre: [],
        decade: [],
        actors: [],
        franchises: []
      };

      // Uncheck all checkboxes
      document.querySelectorAll('[data-filter]').forEach(checkbox => {
        checkbox.checked = false;
      });

      // Clear actor search
      const actorSearch = document.querySelector('.actor-search');
      if (actorSearch) {
        actorSearch.value = '';
        // Show all actor options
        document.querySelectorAll('.actor-list .filter-option').forEach(option => {
          option.style.display = 'block';
        });
      }

      // Show all products
      this.applyFilters();
    });
  }

  bindActorSearch() {
    const searchInput = document.querySelector('.actor-search');

    if (!searchInput) return;

    searchInput.addEventListener('input', (e) => {
      const query = e.target.value.toLowerCase().trim();
      const actorOptions = document.querySelectorAll('.actor-list .filter-option');

      actorOptions.forEach(option => {
        const actorName = option.dataset.actorName || option.textContent.toLowerCase().trim();
        const matches = actorName.includes(query);
        option.style.display = matches ? 'block' : 'none';
      });
    });
  }

  applyFilters() {
    let visibleCount = 0;

    this.productCards.forEach(card => {
      const productDataAttr = card.dataset.metafields;

      // If no metafield data, hide the product when filters are active
      if (!productDataAttr) {
        const hasActiveFilters = Object.values(this.filters).some(arr => arr.length > 0);
        const visible = !hasActiveFilters;
        this.toggleProductVisibility(card, visible);
        if (visible) visibleCount++;
        return;
      }

      let productData;
      try {
        productData = JSON.parse(productDataAttr);
      } catch (e) {
        console.error('Error parsing product metafields:', e);
        this.toggleProductVisibility(card, false);
        return;
      }

      let visible = this.matchesFilters(productData);
      this.toggleProductVisibility(card, visible);

      if (visible) visibleCount++;
    });

    this.updateResultCount(visibleCount);
  }

  matchesFilters(productData) {
    // If no filters are active, show all products
    const hasActiveFilters = Object.values(this.filters).some(arr => arr.length > 0);
    if (!hasActiveFilters) {
      return true;
    }

    let matches = true;

    // Check genre filter (OR logic within filter type)
    if (this.filters.genre.length > 0) {
      const productGenres = productData.genre || [];
      const hasGenreMatch = this.filters.genre.some(selectedGenre =>
        productGenres.includes(selectedGenre)
      );
      matches = matches && hasGenreMatch;
    }

    // Check decade filter (OR logic within filter type)
    if (this.filters.decade.length > 0) {
      const productDecade = productData.decade || '';
      const hasDecadeMatch = this.filters.decade.includes(productDecade);
      matches = matches && hasDecadeMatch;
    }

    // Check actors filter (OR logic within filter type)
    if (this.filters.actors.length > 0) {
      const productActors = productData.actors || [];
      const hasActorMatch = this.filters.actors.some(selectedActor =>
        productActors.includes(selectedActor)
      );
      matches = matches && hasActorMatch;
    }

    // Check franchises filter (OR logic within filter type)
    if (this.filters.franchises.length > 0) {
      const productFranchises = productData.franchises || [];
      const hasFranchiseMatch = this.filters.franchises.some(selectedFranchise =>
        productFranchises.includes(selectedFranchise)
      );
      matches = matches && hasFranchiseMatch;
    }

    return matches;
  }

  toggleProductVisibility(card, visible) {
    // Find the parent list item (which has the padding classes)
    const listItem = card.closest('[role="list-item"]');
    const targetElement = listItem || card;

    if (visible) {
      targetElement.style.display = '';
      targetElement.classList.remove('d-none');
    } else {
      targetElement.style.display = 'none';
      targetElement.classList.add('d-none');
    }
  }

  updateResultCount(visibleCount) {
    const countEl = document.querySelector('.filter-result-count');

    if (!countEl) return;

    // If visibleCount is not provided, calculate it
    if (typeof visibleCount === 'undefined') {
      visibleCount = this.productCards.filter(card => {
        const listItem = card.closest('[role="list-item"]');
        const targetElement = listItem || card;
        return targetElement.style.display !== 'none' && !targetElement.classList.contains('d-none');
      }).length;
    }

    const totalCount = this.productCards.length;
    const hasActiveFilters = Object.values(this.filters).some(arr => arr.length > 0);

    if (hasActiveFilters) {
      countEl.textContent = `Showing ${visibleCount} of ${totalCount} products`;
    } else {
      countEl.textContent = `Showing all ${totalCount} products`;
    }
  }
}

// Initialize filters when the page loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('.collection-filters')) {
      new CollectionFilters();
    }
  });
} else {
  if (document.querySelector('.collection-filters')) {
    new CollectionFilters();
  }
}
